import { useState, memo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, User, Lock, LogIn, XCircle, ChevronRight, ShieldCheck, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { useGoogleLogin } from '@react-oauth/google';

function LoginForm({ onLoginSuccess }) {
  const navigate = useNavigate();
  const [view, setView] = useState("login"); // 'login' | 'forgot-password'
  
  // Login States
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  
  // Forgot Password States
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState("idle"); // 'idle' | 'loading' | 'success' | 'error'
  
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  // --- LOGIN İŞLEMLERİ ---
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Kullanıcı adı veya şifre yanlış.");
      
      localStorage.setItem("token", data.access_token);
      localStorage.setItem("role", "user");
      
      onLoginSuccess();
      navigate("/profile");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- ŞİFRE SIFIRLAMA İSTEĞİ ---
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setResetStatus("loading");
    setError(null);

    try {
      const res = await fetch(`${API_URL}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "İşlem başarısız.");

      setResetStatus("success");
    } catch (err) {
      // Güvenlik gereği bazen hata detayını kullanıcıya göstermeyiz ama şimdilik gösterelim
      setError(err.message);
      setResetStatus("error");
    }
  };

  // Google Login ve Guest Login fonksiyonların aynen korunuyor...
  const handleGoogleLoginSuccess = async (codeResponse) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/google/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: codeResponse.code }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Google ile giriş başarısız.");
      }
      localStorage.setItem("token", data.access_token);
      localStorage.setItem("role", data.role);
      onLoginSuccess();
      navigate("/profile");
    } catch (error) {
      console.error("Sunucu ile iletişimde hata:", error.message);
      setError("Google ile giriş yapılırken bir hata oluştu: " + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const login = useGoogleLogin({
    flow: "auth-code",
    onSuccess: handleGoogleLoginSuccess,
    onError: (err) => setError("Google ile oturum açılamadı."),
  });

  const handleGuestLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_URL}/auth/guest-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Misafir girişi başarısız.");

      localStorage.setItem("token", data.access_token);
      if (data.is_guest !== undefined) localStorage.setItem("is_guest", data.is_guest);
      onLoginSuccess();
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- RENDER: ŞİFREMİ UNUTTUM FORMU ---
  if (view === "forgot-password") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 font-sans text-gray-50">
        <div className="w-full max-w-md bg-gray-800 p-8 rounded-3xl shadow-2xl border border-gray-700">
          <button 
            onClick={() => { setView("login"); setResetStatus("idle"); setError(null); }}
            className="flex items-center text-gray-400 hover:text-white mb-6 transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Giriş Ekranına Dön
          </button>

          <header className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Lock className="h-10 w-10 text-yellow-500" />
              <h2 className="text-2xl font-bold text-gray-50">Şifre Sıfırlama</h2>
            </div>
            <p className="text-gray-400 mt-2 text-sm">
              E-posta adresini gir, sana şifreni sıfırlaman için bir bağlantı gönderelim.
            </p>
          </header>

          {resetStatus === "success" ? (
            <div className="text-center space-y-4 animate-in fade-in zoom-in duration-300">
              <div className="mx-auto w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center border border-green-500/50">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-white">E-posta Gönderildi!</h3>
              <p className="text-gray-400">
                Lütfen gelen kutunu (ve spam klasörünü) kontrol et. Gönderilen linke tıklayarak yeni şifreni belirleyebilirsin.
              </p>
              <button 
                onClick={() => setView("login")}
                className="w-full mt-4 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg transition-colors"
              >
                Tamam
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-6">
               {error && (
                <div className="flex items-center gap-3 p-3 text-red-400 bg-red-900/30 rounded-xl border border-red-700 text-sm">
                  <XCircle className="h-5 w-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <label htmlFor="resetEmail" className="text-sm font-medium text-gray-300">E-posta Adresi</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="resetEmail"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    placeholder="ornek@email.com"
                    className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-gray-600 transition-colors"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={resetStatus === "loading"}
                className={`w-full flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-lg transition-colors 
                  ${resetStatus === "loading" ? 'bg-yellow-800 text-yellow-200 cursor-not-allowed' : 'bg-yellow-600 text-white hover:bg-yellow-700'}`}
              >
                {resetStatus === "loading" ? (
                  <> <Loader2 className="h-5 w-5 animate-spin" /> Gönderiliyor... </>
                ) : (
                  <> Bağlantı Gönder </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // --- RENDER: LOGIN FORMU ---
  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 font-sans text-gray-50">
      <div className="w-full max-w-md bg-gray-800 p-8 rounded-3xl shadow-2xl border border-gray-700">
        <header className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <LogIn className="h-10 w-10 text-blue-400" />
            <h2 className="text-3xl font-bold text-gray-50">Giriş Yap</h2>
          </div>
          <p className="text-gray-400 mt-2">Hesabına giriş yaparak keşiflerine devam et.</p>
        </header>

        {error && (
          <div className="flex items-center gap-3 p-4 mb-6 text-red-400 bg-red-900/30 rounded-xl border border-red-700">
            <XCircle className="h-6 w-6 flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        )}

        <form onSubmit={handleLoginSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="username" className="text-sm font-medium text-gray-300">Kullanıcı Adı</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="username"
                placeholder="Kullanıcı adı veya E-posta"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600 transition-colors"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
                <label htmlFor="password" className="text-sm font-medium text-gray-300">Şifre</label>
                <button 
                  type="button"
                  onClick={() => setView("forgot-password")}
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Şifremi Unuttum?
                </button>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="password"
                id="password"
                placeholder="Şifreniz"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600 transition-colors"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-lg transition-colors 
              ${loading ? 'bg-blue-800 text-blue-200 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {loading ? (
              <> <Loader2 className="h-5 w-5 animate-spin" /> Giriş Yapılıyor... </>
            ) : (
              <> <LogIn className="h-5 w-5" /> Giriş Yap </>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-gray-800 px-3 text-gray-500">VEYA</span>
            </div>
          </div>

          <div className="space-y-4">
            <button
              type="button"
              onClick={login}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-600 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors shadow-sm"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google icon" className="h-5 w-5" />
              Google ile Giriş Yap
            </button>

            <button
              type="button"
              onClick={handleGuestLogin}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-600 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors shadow-sm"
            >
              <ChevronRight className="h-5 w-5" />
              Misafir Olarak Devam Et
            </button>
          </div>

          <div className="mt-6 flex flex-col gap-2">
            <p className="text-gray-400">
              Hesabın yok mu?{" "}
              <Link to="/register" className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">Kayıt Ol</Link>
            </p>
            <Link to="/editor-login" className="inline-flex items-center justify-center gap-1.5 mt-2 text-sm text-gray-500 hover:text-indigo-400 transition-colors">
               <ShieldCheck className="w-4 h-4" />
               Editör Paneli ⮕
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(LoginForm);